import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Trash2 } from "lucide-react";
import api from "../services/api";

const money = (value) => Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const right = { textAlign: "right", fontVariantNumeric: "tabular-nums" };
let nextKey = 1;

/** Products and warehouses for picking returned goods; loaded once per dialog. */
export const useReturnLookups = () => {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/api/v1/products", { size: 1000, sort: "name" }), api.get("/api/v1/warehouses/all")])
      .then(([productPage, warehouseList]) => {
        if (cancelled) return;
        const list = Array.isArray(productPage) ? productPage : productPage?.content || [];
        setProducts(list.filter((p) => p.active !== false));
        setWarehouses(Array.isArray(warehouseList) ? warehouseList : []);
      })
      .catch((error) => toast.error(error.message || "Could not load products / warehouses"));
    return () => {
      cancelled = true;
    };
  }, []);
  return { products, warehouses };
};

const taxPercentOf = (product) => {
  if (!product || product.gstApplicable === false) return 0;
  if (product.salesTaxPercent !== null && product.salesTaxPercent !== undefined) return Number(product.salesTaxPercent);
  return Number(product.cgstPercent || 0) + Number(product.sgstPercent || 0);
};

/** GST % of a bill line, from its tax over its taxable value. */
const itemTaxPercent = (item) => {
  const taxable = Number(item.lineTotal || 0) - Number(item.taxAmount || 0);
  return taxable > 0 ? Math.round((Number(item.taxAmount || 0) * 10000) / taxable) / 100 : 0;
};

/**
 * Value of one return line. A line of the bill is credited at its invoiced value (discount and tax included) in proportion to
 * the quantity; any other product at quantity × rate plus GST (the server values it the same way, from the price list when no
 * rate is entered).
 */
export const returnLineValue = (line, invoiceItems = []) => {
  const quantity = Number(line.quantity || 0);
  if (!quantity) return 0;
  if (line.invoiceItemId) {
    const item = invoiceItems.find((i) => i.id === line.invoiceItemId);
    if (!item || !Number(item.quantity)) return 0;
    return (Number(item.lineTotal || 0) * quantity) / Number(item.quantity);
  }
  const rate = Number(line.unitPrice || 0);
  return quantity * rate * (1 + Number(line.taxPercent || 0) / 100);
};

export const returnLinesTotal = (lines, invoiceItems = []) => lines.reduce((sum, line) => sum + returnLineValue(line, invoiceItems), 0);

/** A bill line listed with no quantity typed: not being returned (left out when saving). */
const isBlankBillLine = (line) => line.invoiceItemId && (line.quantity === "" || line.quantity === null || line.quantity === undefined);

/** What can still be returned on a bill line (less returns waiting for approval). */
const returnableOf = (item) =>
  item.returnableQuantity !== undefined && item.returnableQuantity !== null
    ? Math.max(0, Number(item.returnableQuantity))
    : Math.max(0, Number(item.quantity || 0) - Number(item.returnedQuantity || 0));

/**
 * Share of a bill line that {@code quantity} carries: total, scheme and cash discount, CGST / SGST / IGST (as the server credits
 * it, in proportion to the quantity).
 */
export const returnShare = (item, quantity) => {
  const ratio = Number(item?.quantity) ? Number(quantity || 0) / Number(item.quantity) : 0;
  const part = (value) => Math.round(Number(value || 0) * ratio * 100) / 100;
  return {
    total: part(item?.lineTotal),
    scheme: part(item?.schemeDiscountAmount ?? item?.discountAmount),
    cash: part(item?.cashDiscountAmount),
    cgst: part(item?.cgstAmount),
    sgst: part(item?.sgstAmount),
    igst: part(item?.igstAmount),
  };
};

/** Lines as the server expects them (ReturnLineRequest / FullReversalExtraItem); bill lines with no quantity are left out. */
export const toReturnLineRequests = (lines) =>
  lines.filter((line) => !isBlankBillLine(line)).map((line) => ({
    invoiceItemId: line.invoiceItemId || null,
    productId: line.invoiceItemId ? null : Number(line.productId),
    quantity: Number(line.quantity),
    unitPrice: line.invoiceItemId || line.unitPrice === "" || line.unitPrice === null ? null : Number(line.unitPrice),
    warehouseId: line.warehouseId ? Number(line.warehouseId) : null,
  }));

/**
 * Checks the lines before they are sent; returns an error message or null. {@code checkReturnable} false (debit notes: goods
 * supplied) does not limit a bill item to what can still be returned.
 */
export const validateReturnLines = (lines, invoiceItems = [], { checkReturnable = true } = {}) => {
  const used = lines.filter((line) => !isBlankBillLine(line));
  if (!used.length) return "Enter the quantity returned on at least one product";
  for (const line of used) {
    if (!line.invoiceItemId && !line.productId) return "Select the product on every line";
    if (!(Number(line.quantity) > 0)) return "Enter a quantity on every line";
    if (line.invoiceItemId && checkReturnable) {
      const item = invoiceItems.find((i) => i.id === line.invoiceItemId);
      const returnable = item ? returnableOf(item) : 0;
      if (Number(line.quantity) > returnable + 1e-9) return `${item?.productName || "Item"}: only ${returnable} can still be returned`;
    }
  }
  return null;
};

/**
 * Goods taken back: lines of the bill (`invoiceItems`, when `allowInvoiceItems`) and / or any other product — mostly expired
 * stock — with quantity, rate and the warehouse each goes into. The value of every line and the total are worked out as the
 * user types.
 */
const ReturnLinesEditor = ({
  lines,
  onChange,
  products,
  warehouses,
  invoiceItems = [],
  allowInvoiceItems = true,
  otherLabel = "Add other product",
  prefillInvoiceItems = false,
  // debit note: goods supplied (out of the warehouse), not limited to what can be returned
  debit = false,
}) => {
  const update = (key, patch) => onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  const remove = (key) => onChange(lines.filter((line) => line.key !== key));

  const returnable = debit ? () => Infinity : returnableOf;

  // the bill's items listed straight away, quantity blank: typing a quantity returns that much
  const prefilled = useRef(false);
  useEffect(() => {
    if (!prefillInvoiceItems || prefilled.current || !invoiceItems.length || lines.length) return;
    prefilled.current = true;
    onChange(
      invoiceItems
        .filter((item) => returnable(item) > 0)
        .map((item) => ({ key: nextKey++, invoiceItemId: item.id, productId: item.productId, quantity: "", unitPrice: item.unitPrice, warehouseId: "" }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillInvoiceItems, invoiceItems]);

  const showsIgst = invoiceItems.some((item) => Number(item.igstAmount) > 0);
  const unusedItems = invoiceItems.filter((item) => returnable(item) > 0 && !lines.some((line) => line.invoiceItemId === item.id));

  const addInvoiceItem = (itemId) => {
    const item = invoiceItems.find((i) => String(i.id) === String(itemId));
    if (!item) return;
    onChange([
      ...lines,
      { key: nextKey++, invoiceItemId: item.id, productId: item.productId, quantity: String(returnable(item)), unitPrice: item.unitPrice, warehouseId: "" },
    ]);
  };
  const addOther = () => onChange([...lines, { key: nextKey++, invoiceItemId: null, productId: "", quantity: "", unitPrice: "", taxPercent: 0, warehouseId: "" }]);

  const pickProduct = async (key, productId) => {
    const product = products.find((p) => String(p.id) === String(productId));
    update(key, { productId, taxPercent: taxPercentOf(product), unitPrice: "" });
    if (!productId) return;
    try {
      // suggest the rate from the product's active price list; it can be changed
      const priceLists = await api.get("/api/v1/products/price-lists", { productId });
      const active = (Array.isArray(priceLists) ? priceLists : []).find((p) => p.active && Number(p.basePrice) > 0);
      if (active) update(key, { productId, taxPercent: taxPercentOf(product), unitPrice: String(active.basePrice) });
    } catch (error) {
      console.warn("Could not load the price list", error);
    }
  };

  const total = returnLinesTotal(lines, invoiceItems);

  return (
    <div>
      <div className="table-wrapper" style={{ marginTop: 0 }}>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: "left", minWidth: 220 }}>Product</th>
              <th style={right}>Qty</th>
              <th style={right} title="Rate before tax; leave blank to use the price list">
                Rate
              </th>
              <th style={right}>GST %</th>
              <th style={right} title="Scheme discount on the quantity returned">Scheme Amt</th>
              <th style={right} title="Cash discount on the quantity returned">Cash Amt</th>
              {showsIgst ? (
                <th style={right}>IGST Amt</th>
              ) : (
                <>
                  <th style={right}>CGST Amt</th>
                  <th style={right}>SGST Amt</th>
                </>
              )}
              <th style={{ minWidth: 150 }}>{debit ? "Take from warehouse" : "Return to warehouse"}</th>
              <th style={right}>Total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={showsIgst ? 10 : 11} className="page-subtitle" style={{ textAlign: "center" }}>
                  No products added
                </td>
              </tr>
            ) : (
              lines.map((line) => {
                const item = line.invoiceItemId ? invoiceItems.find((i) => i.id === line.invoiceItemId) : null;
                const share = item && Number(line.quantity) > 0 ? returnShare(item, line.quantity) : null;
                const shown = (value) => (share ? money(value) : "-");
                return (
                  <tr key={line.key}>
                    <td style={{ textAlign: "left" }}>
                      {item ? (
                        <span title="Line of this bill">
                          {item.productName}{" "}
                          <span className="page-subtitle">({item.skuCode}{debit ? "" : `, returnable ${returnable(item)}`})</span>
                        </span>
                      ) : (
                        <select value={line.productId} onChange={(e) => pickProduct(line.key, e.target.value)} style={{ width: "100%" }}>
                          <option value="">-- Select product --</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.skuCode})
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td style={right}>
                      <input type="number" min="0" step="any" value={line.quantity} onChange={(e) => update(line.key, { quantity: e.target.value })}
                        placeholder={item && !debit ? `max ${returnable(item)}` : ""} style={{ width: 80, textAlign: "right" }} />
                    </td>
                    <td style={right}>
                      {item ? (
                        money(item.unitPrice)
                      ) : (
                        <input type="number" min="0" step="any" value={line.unitPrice} placeholder="Price list"
                          onChange={(e) => update(line.key, { unitPrice: e.target.value })} style={{ width: 95, textAlign: "right" }} />
                      )}
                    </td>
                    <td style={right}>{item ? itemTaxPercent(item) : Number(line.taxPercent || 0)}</td>
                    <td style={right}>{shown(share?.scheme)}</td>
                    <td style={right}>{shown(share?.cash)}</td>
                    {showsIgst ? (
                      <td style={right}>{shown(share?.igst)}</td>
                    ) : (
                      <>
                        <td style={right}>{shown(share?.cgst)}</td>
                        <td style={right}>{shown(share?.sgst)}</td>
                      </>
                    )}
                    <td>
                      <select value={line.warehouseId} onChange={(e) => update(line.key, { warehouseId: e.target.value })} style={{ width: "100%" }}>
                        <option value="">Default warehouse</option>
                        {warehouses.map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name} {w.type ? `(${w.type === "GOOD" ? "Good" : "Bad"})` : ""}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={right}>₹{money(returnLineValue(line, invoiceItems))}</td>
                    <td>
                      <button type="button" className="secondary" onClick={() => remove(line.key)} title="Remove" style={{ padding: "4px 8px" }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {lines.length ? (
            <tfoot>
              <tr>
                <td colSpan={showsIgst ? 8 : 9} style={{ textAlign: "right", fontWeight: 700 }}>
                  Total value {lines.some((l) => !l.invoiceItemId && l.unitPrice === "") ? "(lines without a rate are valued from the price list)" : ""}
                </td>
                <td style={{ ...right, fontWeight: 700 }}>₹{money(total)}</td>
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <div className="flex gap-2" style={{ marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
        {allowInvoiceItems && invoiceItems.length ? (
          <select value="" onChange={(e) => addInvoiceItem(e.target.value)} disabled={!unusedItems.length} aria-label="Add a line of this bill">
            <option value="">{unusedItems.length ? "+ Add item from this bill" : "All bill items added"}</option>
            {unusedItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.productName} ({item.skuCode}){debit ? "" : ` — returnable ${returnable(item)}`}
              </option>
            ))}
          </select>
        ) : null}
        <button type="button" className="secondary" onClick={addOther} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Plus size={14} /> {otherLabel}
        </button>
      </div>
    </div>
  );
};

export default ReturnLinesEditor;
